import { el, VNode } from "@dhx/ts-common/dom";
import { getStrSize } from "@dhx/ts-common/html";
import { ShapesCollection, IBaseShape, ITopicShapeConfig } from "@dhx/ts-diagram";
import { BaseShape } from "./BaseShape";
import { getCircleTpl, getShapeCss } from "../templates/templates";
import { getMeta } from "../../helpers/baseMetaInfo";
import locale from "../../locales/en";

export class TopicShape extends BaseShape implements IBaseShape {
	config: ITopicShapeConfig;
	data: ShapesCollection;
	private _oldText: string;

	constructor(config, parameters?) {
		super(config, parameters);
		this.data = parameters.data;
	}
	public getMetaInfo(): { [key: string]: string }[] {
		return getMeta([
			{ type: "grid", label: locale.gridStep },
			{ type: "position", label: locale.position },
			{ type: "size", label: locale.size },
			{ type: "fill", label: locale.fill },
			{ type: "strokeProps", label: locale.stroke },
			{ type: "text", label: locale.text },
			{ type: "textProps", label: locale.textProps },
		]);
	}
	public render(): VNode {
		const coords = this.getCoords(this.config);
		const {
			id,
			angle,
			width,
			height,
			fontSize,
			lineHeight,
			fontWeight,
			fontStyle,
			text,
			fill,
			stroke,
			strokeWidth,
			strokeType,
			$editable,
			$group,
			$selected,
		} = this.config;

		const zIndex = $group || $selected ? ($selected ? 2 : 1) : 0;

		const textStyle = {
			fontSize: `${fontSize}px`,
			fontFamily: "Roboto, Arial, Tahoma, Verdana, sans-serif",
			lineHeight: `${lineHeight}px`,
			fontWeight,
			fontStyle,
			strokeWidth,
		};
		if (this._oldText && this.config.text !== this._oldText) {
			const stringSize = getStrSize(text, textStyle);
			const strWidth = stringSize.width + 16;
			const strHeight = stringSize.height + 8;
			this.data.update(this.id, {
				height: strHeight > 30 ? strHeight : 30,
				width: strWidth > 60 ? strWidth : 60,
			});
		}

		this._oldText = this.config.text;

		const circle = [];
		if (Array.isArray(this.config.dir)) {
			this.config.dir.forEach((_dir, index) => {
				circle.push(getCircleTpl(this.config, index));
			});
		} else {
			circle.push(getCircleTpl(this.config));
		}

		let borderStyle = "none";
		if (strokeType === "line" || !strokeType) borderStyle = "solid";
		if (strokeType === "dash") borderStyle = "dashed";

		return el(
			"div",
			{
				dhx_id: id,
				class: "dhx_diagram_item dhx_diagram_shape " + this.getCss(),
				style: {
					position: "absolute",
					left: coords.x,
					top: coords.y,
					transform: `rotate(${angle || 0}deg)`,
					zIndex,
				},
			},
			[
				el(
					"div.dhx_diagram_item__text",
					{
						style: {
							height,
							width,
							border: `${strokeWidth}px ${borderStyle} ${stroke || "#03A9F4"}`,
							background: fill,
							borderRadius: `${height / 2}`,
							...getShapeCss(this.config),
						},
					},
					$editable ? [this.getEditorNode()] : text
				),
				...circle,
			]
		);
	}
	protected setDefaults(config: ITopicShapeConfig, defaults: ITopicShapeConfig): ITopicShapeConfig {
		const {
			width,
			height,
			fontColor,
			fontSize,
			fontStyle,
			textAlign,
			lineHeight,
			textVerticalAlign,
			text,
			fontWeight,
			x,
			y,
			fill,
			strokeWidth,
			stroke,
			fixed,
			editable,
		} = config;

		const lineHeightDefault = defaults.lineHeight ? parseFloat(defaults.lineHeight as any) : 14;
		const fontSizeDefault = defaults.fontSize ? parseFloat(defaults.fontSize as any) : 14;
		const strokeWidthDefault = defaults.strokeWidth ? parseFloat(defaults.strokeWidth as any) : 1;

		config.lineHeight = lineHeight || lineHeightDefault;
		config.fontSize = fontSize || fontSizeDefault;
		config.text = text || defaults.text || "";
		config.fill = fill || defaults.fill || "#fff";
		config.fontColor = fontColor || defaults.fontColor || "rgba(0,0,0,0.70)";
		config.textAlign = textAlign || defaults.textAlign || "center";
		config.fontStyle = fontStyle || defaults.fontStyle || "normal";
		config.textVerticalAlign = textVerticalAlign || defaults.textVerticalAlign || "center";
		config.strokeWidth = strokeWidth || strokeWidthDefault;
		config.stroke = stroke || defaults.stroke;

		const textStyle = {
			fontSize: `${fontSize || fontSizeDefault}px`,
			fontFamily: "Roboto, Arial, Tahoma, Verdana, sans-serif",
			lineHeight: `${lineHeight || lineHeightDefault}px`,
			fontWeight,
			fontStyle,
			strokeWidth: config.strokeWidth,
		};
		const stringSize = getStrSize(text, textStyle);

		const widthDefault = defaults.width ? parseFloat(defaults.width as any) : stringSize.width + 24;
		const heightDefault = defaults.height ? parseFloat(defaults.height as any) : stringSize.height + 12;

		config.height = heightDefault > 30 || height > 30 ? height || heightDefault : 30;
		config.width = widthDefault > 60 || width > 60 ? width || widthDefault : 60;

		config.x = x || 0;
		config.y = y || 0;

		config.fixed = fixed || false;
		config.editable = typeof editable === "boolean" ? editable : true;

		return config;
	}
}
